import { type ReactNode, useRef } from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import ErrorBoundary from "simplydo/react/components/Error/ErrorBoundary";
import { cx, styled } from "simplydo/react/styled";
import { belowComputer, computerUp } from "simplydo/react/theme";
import { useTheme } from "simplydo/react/theme";
import { Accordion, Container } from "simplydo/react/ui";

import { Sticky } from "components/lib/UI";

import SideNavLink, { type SideNavLinkProps } from "./SideNavLink";

const StyledContainer = styled.div`
  flex: 1;
  display: flex;

  ${belowComputer} {
    flex-direction: column;
  }

  ${computerUp} {
    flex-direction: row;

    &.has-main.is-fluid {
      margin: 0 2rem;
    }
  }
`;

const StyledSideBar = styled.div`
  flex: 1;
  background-color: transparent;

  &.is-empty {
    background-color: rgba(0, 0, 0, 0.04);
  }

  &.is-collapsed {
    flex: 0 0 42px;
  }

  ${belowComputer} {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }

  ${computerUp} {
    &.has-main {
      flex: 0 0 25%;
      min-height: 80vh;
      max-width: 25%;

      &.is-fluid {
        flex: 0 0 15%;
      }
    }
  }
`;

const StyledMain = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  max-width: 100%;
  min-width: 0;

  ${belowComputer} {
    padding: 10px;
  }

  ${computerUp} {
    flex: 1 1 25%;
    margin-left: 15px;
    margin-top: 15px;
    padding-bottom: 15px;

    &.is-fluid {
      flex: 1 1 15%;
    }

    &.is-fluid-very {
      margin-top: 0;
    }
  }

  > div:not(.stackable) {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
`;

const MobileContainer = styled.div`
  width: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;
`;

type SideNavCustomProps = { content: ReactNode };

export type SideNavComponentCollapsibleProps = {
  // Controlled collapsible
  // Custom nav items are not displayed in collapsible side nav links
  collapsible: boolean;
  isCollapsed: boolean;
  onCollapse: () => void;
  onExpand: () => void;
};

export type SideNavItemProps = { key: string } & (
  | ({ type: "link" } & SideNavLinkProps)
  | ({ type: "custom" } & SideNavCustomProps)
) &
  Partial<SideNavComponentCollapsibleProps>;

type SideNavComponentProps = {
  nav?: ReactNode;
  navItems?: (SideNavItemProps | null)[];
  main: ReactNode;
  fluid?: boolean | "very";
  top?: number;
  loading?: boolean;
  afterContent?: ReactNode;
  beforeContent?: ReactNode;
  scrolling?: boolean;
} & Partial<SideNavComponentCollapsibleProps>;

const StyledSticky = styled(Sticky)<{ top: number }>`
  ${belowComputer} {
    padding-top: 10px;
    padding-bottom: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
  }

  ${computerUp} {
    &.is-scrolling {
      max-height: calc(100vh - ${({ top }) => top || 120}px);
      overflow-y: auto;
      overflow-x: hidden;
      overscroll-behavior: contain;
      padding-top: 15px;
      padding-bottom: 15px;
      padding-right: 8px;
      padding-left: 2px;
      scrollbar-width: thin;
      scrollbar-color: rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0);

      &:hover {
        scrollbar-color: rgba(0, 0, 0, 0.35) rgba(0, 0, 0, 0);
      }
    }
  }
`;

type SideNavContainerProps = {
  showMobileView: boolean;
  children: ReactNode;
  top: number;
  scrolling: boolean;
};

const SideNavContainer = ({
  showMobileView,
  children,
  top,
  scrolling,
}: SideNavContainerProps) => {
  if (showMobileView) {
    return (
      <Accordion
        styled
        fluid
        panels={[
          {
            key: "navigation",
            title: "Navigation",
            content: { content: children },
          },
        ]}
      />
    );
  }
  return (
    <StyledSticky
      top={top}
      bottom={undefined}
      className={cx(scrolling && "is-scrolling")}
    >
      {children}
    </StyledSticky>
  );
};

const SideNav = ({
  nav,
  navItems,
  main,
  fluid,
  top,
  loading,
  collapsible,
  isCollapsed,
  onCollapse,
  onExpand,
  afterContent,
  beforeContent,
}: SideNavComponentProps) => {
  const theme = useTheme();
  const location = useLocation();
  const { t } = useTranslation();
  const ContainerComponent =
    !theme.sizes.isComputer && !!main ? MobileContainer : Container;
  // Remove empty nav items
  const filteredNavItems =
    navItems ? navItems.filter((item) => item !== null) : null;

  const containerRef = useRef<HTMLDivElement>(null);
  const topOffset = top !== undefined ? top : 120;

  const shouldShowCollapsed = isCollapsed && !theme.sizes.isMobile;
  return (
    <ContainerComponent
      ref={containerRef}
      fluid={!!fluid}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <StyledContainer
        className={cx(!!main && "has-main", !!fluid && "is-fluid")}
      >
        {!filteredNavItems?.length && !nav && !beforeContent && !afterContent ?
          null
        : <StyledSideBar
            className={cx(
              shouldShowCollapsed && "is-collapsed",
              !!main && "has-main",
              !!fluid && "is-fluid",
              !navItems?.length && "is-empty",
            )}
          >
            <SideNavContainer
              showMobileView={!theme.sizes.isComputer && !!main}
              top={topOffset}
              scrolling={!!main}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {beforeContent ? beforeContent : null}
                {nav ? nav : null}
                {(filteredNavItems ?? []).map((item) =>
                  item.type === "custom" ?
                    item.content
                  : <SideNavLink
                      {...item}
                      onClick={() => {
                        item?.onClick?.();
                        const bounds =
                          containerRef.current?.getBoundingClientRect();

                        if (bounds && bounds.top - topOffset < 0) {
                          document.body.parentElement?.scrollTo({
                            top:
                              document.body.parentElement.scrollTop +
                              bounds.top -
                              topOffset +
                              10,
                            behavior: "instant",
                          });
                        }
                      }}
                      key={item.key}
                      collapsible={
                        theme.sizes.isComputer ? !!collapsible : false
                      }
                      isCollapsed={isCollapsed}
                      onCollapse={onCollapse}
                      onExpand={onExpand}
                    />,
                )}
                {(
                  collapsible &&
                  onCollapse &&
                  onExpand &&
                  theme.sizes.isComputer &&
                  !loading
                ) ?
                  <div style={{ marginTop: 15 }}>
                    <SideNavLink
                      key="collapse"
                      onClick={isCollapsed ? onExpand : onCollapse}
                      content={
                        isCollapsed ?
                          t("sidebar.expand")
                        : t("sidebar.collapse")
                      }
                      isCollapsed={isCollapsed}
                      iconLeft={
                        isCollapsed ? "angle double right" : "angle double left"
                      }
                      active={false}
                      secondary
                      compact="very"
                    />
                  </div>
                : null}
                {afterContent ? afterContent : null}
              </div>
            </SideNavContainer>
          </StyledSideBar>}
        {main ?
          <ErrorBoundary
            padding="0px 0px 0px 10px"
            pathName={location.pathname}
          >
            <StyledMain
              className={cx(
                !!fluid && "is-fluid",
                fluid === "very" && "is-fluid-very",
              )}
            >
              {main}
            </StyledMain>
          </ErrorBoundary>
        : null}
      </StyledContainer>
    </ContainerComponent>
  );
};

export default SideNav;
